import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Collapse,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton, Tooltip } from "@mui/material";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import CustomTypography from "../../globalStyles/fonts";
import { isMobile } from "../../globalStyles/mobile";
import { toast } from "react-toastify";
import Pagination from "../pagination";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { oauthClientService } from "../../utils/services/o-auth-client";
import AuthenticateModal from "../client-authorization-modal";
import { formatDateTime } from "../../utils/formatDate";
import TokenGenerationModal from "../generate-token";

const OAuthClientList = ({
  viewMode,
  data,
  onDeleteData,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  selectedAuthClient,
  setSelectedAuthClient,
}) => {
  const [openAuthenticateModal, setOpenAuthenticateModal] = useState(false);
  const [openTokenModal, setOpenTokenModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [clientDetails, setClientDetails] = useState({});
  const [tokenGeneratedClientId, setTokenGeneratedClientId] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [expandedUris, setExpandedUris] = useState({});
  const startIndex = rowsPerPage * page;
  const code = localStorage.getItem("oauth_code");
  const isStateValid = localStorage.getItem("oauth_state_valid") === "true";
  const [regenerateContext, setRegenerateContext] = useState({});

  useEffect(() => {
    localStorage.removeItem("oauth_code");
    localStorage.removeItem("oauth_state");
    localStorage.removeItem("authenticatedClientId");

    return () => {
      localStorage.removeItem("oauth_code");
      localStorage.removeItem("oauth_state");
      localStorage.removeItem("authenticatedClientId");
    };
  }, []);

  const toggleShowMore = (id) => {
    setExpandedUris((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleRowClick = async (e, client) => {
    const id = client._id;

    if (expandedRowId === id) {
      setExpandedRowId(null);
      return;
    }

    try {
      const res = await oauthClientService.fetchToken("", "", client.client_id);

      const tokenList = res.data || [];
      setClientDetails(res.data);

      setExpandedRowId(id);
    } catch (err) {
      console.error("Failed to fetch client details:", err);
    }
  };

  const showGenerateToken = code && isStateValid;

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "var(--table-header-bg)",
      border: "1px solid var(--border-color)",
      padding: "6px",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
      padding: "6px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "var(--bg-paper)",
      border: "1px solid var(--border-color)",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "var(--bg-secondary)",
      border: "1px solid var(--border-color)",
    },
    "&:lastChild td, &:lastChild th": {
      border: 0,
    },
  }));

  const deleteData = async () => {
    try {
      await oauthClientService.delete(deletingItemId);
      setOpenConfirmation(false);
      onDeleteData && onDeleteData();
      toast.success("Client deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete");
      console.error("Delete failed:", error);
    }
  };

  const handleCopy = (token, type) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(type);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    setSelectedAuthClient(item);
    setDeletingItemId(item._id);
    setOpenConfirmation(true);
  };
  const handleAuthenticateClick = (e, item) => {
    e.stopPropagation();
    setSelectedAuthClient(item);
    setOpenAuthenticateModal(true);
  };
  const handleGenerateToken = async (e, client, token) => {
    e.stopPropagation();
    setSelectedAuthClient(client);
    setOpenTokenModal(true);
    setRegenerateContext(token);
  };

  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {data?.map((x, index) => {
        const redirectUris = x?.redirect_uris || [];
        const showCollapse = redirectUris.length > 2;
        const isExpanded = expandedUris[x._id];

        return (
          <Grid
            sx={{ paddingBottom: 1 }}
            item
            xs={12}
            sm={6}
            md={4}
            key={index}
          >
            <Card
              sx={{
                boxShadow: 3,
                cursor: "pointer",
                width: "100%",
                height: "100%",
                position: "relative",
                borderRadius: "6px",
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    {startIndex + index + 1}
                  </Typography>
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 5,
                    }}
                  >
                    <Button
                      style={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                      }}
                      aria-label="delete"
                      onClick={(e) => handleDeleteClick(e, x)}
                    >
                      <DeleteIcon />
                    </Button>
                  </div>
                </div>

                <Grid container spacing={1} mt={1}>
                  <Grid item xs={5} display="flex" alignItems="center">
                    <Typography
                      fontWeight="500"
                      sx={{ textAlign: "start" }}
                      fontFamily="Poppins"
                      color="var(--text-tertiary)"
                    >
                      Client Id
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography
                      noWrap
                      sx={{
                        textAlign: "start",
                        fontWeight: 500,
                        fontFamily: "Poppins",
                        color: "var(--text-tertiary)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={x?.client_id}
                    >
                      : {x?.client_id}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={1} mt={1} alignItems="flex-start">
                  <Grid item xs={5} display="flex" alignItems="center">
                    <Typography
                      fontWeight="500"
                      sx={{ textAlign: "start" }}
                      fontFamily="Poppins"
                      color="var(--text-tertiary)"
                    >
                      Redirect Uris
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={7}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    {redirectUris.slice(0, 2).map((uri, idx) => (
                      <Typography
                        key={idx}
                        sx={{
                          textAlign: "start",
                          fontWeight: 500,
                          fontFamily: "Poppins",
                          color: "var(--text-tertiary)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                        title={uri}
                      >
                        : {uri}
                      </Typography>
                    ))}
                    <Collapse in={isExpanded}>
                      {redirectUris.slice(2).map((uri, idx) => (
                        <Typography
                          key={idx + 2}
                          sx={{
                            textAlign: "start",
                            fontWeight: 500,
                            fontFamily: "Poppins",
                            color: "var(--text-tertiary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                          }}
                          title={uri}
                        >
                          : {uri}
                        </Typography>
                      ))}
                    </Collapse>

                    {showCollapse && (
                      <Button
                        onClick={() => toggleShowMore(x._id)}
                        size="small"
                        sx={{
                          display: "flex",
                          alignSelf: "end",
                          textTransform: "none",
                        }}
                      >
                        {isExpanded ? "Show less" : "Show more..."}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </CardContent>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: 1,
                  mr: 1,
                }}
              >
                {tokenGeneratedClientId === x._id ? (
                  <Typography
                    sx={{
                      fontFamily: "poppins",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#007613",
                      alignSelf: "center",
                      paddingRight: "8px",
                    }}
                  >
                    Token Generated
                  </Typography>
                ) : showGenerateToken && x._id === selectedAuthClient?._id ? (
                  <Button
                    onClick={(e) => handleGenerateToken(e, x)}
                    sx={{
                      fontFamily: "poppins",
                      fontSize: "12px",
                      fontWeight: 500,
                      textTransform: "none",
                      backgroundColor: "#007613",
                    }}
                    variant="contained"
                  >
                    Generate Token
                  </Button>
                ) : (
                  <Button
                    onClick={(e) => handleAuthenticateClick(e, x)}
                    sx={{
                      fontFamily: "poppins",
                      fontSize: "12px",
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                    variant="contained"
                  >
                    Authenticate
                  </Button>
                )}
              </Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", height: "300px" }}
      />
    </div>
  );

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        paddingBottom: 20,
        marginTop: 5,
      }}
    >
      {isMobile() ? (
        renderGridView()
      ) : (
        <TableContainer
          sx={{
            overflowY: "auto",
            paddingInline: 0.4,
            maxHeight: "100%",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{
              border: "1px solid var(--border-color)",
            }}
          >
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>
                  <Typography
                    fontSize="14px"
                    color="var(--text-tertiary)"
                    fontWeight={500}
                    fontFamily={"poppins"}
                  >
                    SL NO
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography
                    fontSize="14px"
                    color="var(--text-tertiary)"
                    fontWeight={500}
                    fontFamily={"poppins"}
                  >
                    CLIENT ID
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <Typography
                    fontSize="14px"
                    color="var(--text-tertiary)"
                    fontWeight={500}
                    fontFamily={"poppins"}
                  >
                    REDIRECT URIS
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <CustomTypography
                    fontSize="14px"
                    title={true}
                    color="var(--text-tertiary)"
                    fontWeight={500}
                  ></CustomTypography>
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <StyledTableRow>
                  <TableCell colSpan={8} align="center">
                    <NoDataImage />
                  </TableCell>
                </StyledTableRow>
              ) : (
                data.map((x, index) => (
                  <>
                    <StyledTableRow
                      key={index}
                      onClick={(e) => handleRowClick(e, x)}
                      style={{ cursor: "pointer" }}
                    >
                      <StyledTableCell>
                        <Typography
                          fontSize="12px"
                          color="var(--text-secondary)"
                          fontWeight={400}
                          fontFamily={"Poppins"}
                          style={{ marginLeft: 7 }}
                        >
                          {startIndex + index + 1}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          fontSize="12px"
                          color="var(--text-secondary)"
                          fontWeight={400}
                          fontFamily={"Poppins"}
                          style={{ marginLeft: 7 }}
                        >
                          {x?.client_id}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <div style={{ marginLeft: 7 }}>
                          {x?.redirect_uris?.map((uri, index) => (
                            <Typography
                              key={index}
                              fontSize="12px"
                              color="var(--text-secondary)"
                              fontWeight={400}
                              fontFamily={"Poppins"}
                            >
                              {uri}
                            </Typography>
                          ))}
                        </div>
                      </StyledTableCell>

                      <StyledTableCell
                        sx={{
                          width: "2%",
                          textAlign: "center",
                        }}
                      >
                        <Box display="flex" flexDirection="row" padding="8px">
                          {tokenGeneratedClientId === x._id ? (
                            <Typography
                              sx={{
                                fontFamily: "poppins",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#007613",
                                alignSelf: "center",
                                paddingRight: "8px",
                              }}
                            >
                              Token Generated
                            </Typography>
                          ) : showGenerateToken &&
                            x._id === selectedAuthClient?._id ? (
                            <Button
                              onClick={(e) => handleGenerateToken(e, x)}
                              sx={{
                                fontFamily: "poppins",
                                fontSize: "12px",
                                fontWeight: 500,
                                textTransform: "none",
                                backgroundColor: "#007613",
                              }}
                              variant="contained"
                            >
                              Generate Token
                            </Button>
                          ) : (
                            <Button
                              onClick={(e) => handleAuthenticateClick(e, x)}
                              sx={{
                                fontFamily: "poppins",
                                fontSize: "12px",
                                fontWeight: 500,
                                textTransform: "none",
                              }}
                              variant="contained"
                            >
                              Authenticate
                            </Button>
                          )}

                          <Button
                            style={{
                              justifyContent: "flex-start",
                              textTransform: "none",
                            }}
                            aria-label="delete"
                            onClick={(e) => handleDeleteClick(e, x)}
                          >
                            <DeleteIcon />
                          </Button>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <TableCell
                        colSpan={5}
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={expandedRowId === x._id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box margin={2}>
                            {(clientDetails || []).length > 0 ? (
                              <>
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  mb={2}
                                >
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={(e) =>
                                      handleGenerateToken(
                                        e,
                                        x,
                                        clientDetails[0],
                                      )
                                    }
                                    sx={{
                                      fontFamily: "poppins",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      textTransform: "none",
                                    }}
                                  >
                                    Regenerate Token
                                  </Button>
                                </Box>
                                {clientDetails.map((detail, i) => (
                                  <Box
                                    key={i}
                                    mb={3}
                                    p={2}
                                    border={1}
                                    borderColor="grey.300"
                                    borderRadius={2}
                                  >
                                    <Stack spacing={1}>
                                      <Box display="flex" alignItems="center">
                                        <Typography
                                          fontSize="14px"
                                          fontFamily="Poppins"
                                          fontWeight={400}
                                          sx={{
                                            maxWidth: 350,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            display: "block",
                                          }}
                                        >
                                          Access Token:{" "}
                                          {detail.access_token || "N/A"}
                                        </Typography>

                                        {detail.access_token && (
                                          <Tooltip
                                            title={
                                              copiedToken === "access"
                                                ? "Access Token Copied!"
                                                : ""
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleCopy(
                                                  detail.access_token,
                                                  "access",
                                                )
                                              }
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>

                                      <Typography
                                        fontSize="14px"
                                        fontFamily="Poppins"
                                        fontWeight={400}
                                      >
                                        Access Token Expires At:{" "}
                                        {formatDateTime(
                                          detail.access_token_expires_at,
                                          "N/A",
                                        )}
                                      </Typography>

                                      <Box display="flex" alignItems="center">
                                        <Typography
                                          fontSize="14px"
                                          fontFamily="Poppins"
                                          fontWeight={400}
                                          sx={{
                                            maxWidth: 350,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            display: "block",
                                          }}
                                        >
                                          Refresh Token:{" "}
                                          {detail.refresh_token || "N/A"}
                                        </Typography>
                                        {detail.refresh_token && (
                                          <Tooltip
                                            title={
                                              copiedToken === "refresh"
                                                ? "Refresh Token Copied!"
                                                : ""
                                            }
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleCopy(
                                                  detail.refresh_token,
                                                  "refresh",
                                                )
                                              }
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Stack>
                                  </Box>
                                ))}
                              </>
                            ) : (
                              <Typography fontSize="13px">
                                No token details available.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </StyledTableRow>
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? "60vh" : `calc(100vh - 260px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::WebkitScrollbar": { width: 0 },
        }}
      >
        {viewMode === "grid" ? renderGridView() : renderListView()}
      </div>
      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= rowsPerPage * (page + 1)}
      />
      <DeleteConfirmationModal
        DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedAuthClient?.client_id} ?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={() => {
          deleteData();
        }}
      />
      <AuthenticateModal
        open={openAuthenticateModal}
        onClose={() => setOpenAuthenticateModal(false)}
        client={selectedAuthClient}
      />
      <TokenGenerationModal
        open={openTokenModal}
        onClose={() => setOpenTokenModal(false)}
        client={selectedAuthClient}
        setTokenGeneratedClientId={(id) => {
          setTokenGeneratedClientId(id);
          setOpenTokenModal(false);
          setRegenerateContext({});
          localStorage.setItem("oauth_code", "");
          localStorage.setItem("oauth_state_valid", "false");
          localStorage.removeItem("oauth_selected_uri");
          localStorage.removeItem("oauth_code");
          localStorage.removeItem("oauth_state");
          localStorage.removeItem("authenticatedClientId");
        }}
        regenerateContext={regenerateContext}
      />
    </div>
  );
};

export default OAuthClientList;
